import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatApiError, fmtDate } from "@/lib/api";
import { Wrench, Gauge, Clock, ClipboardList, ShieldCheck, ClipboardCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import PreStartForm from "@/components/PreStartForm";

export default function PublicMachine() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");
  const [showPreStart, setShowPreStart] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/public/machines/${id}`)
      .then(({ data }) => setM(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail) || "Machine not found"));
  }, [id]);

  const onPreStartDone = (data) => {
    setResult(data);
    setShowPreStart(false);
    api.get(`/public/machines/${id}`).then(({ data }) => setM(data)).catch(() => {});
  };

  const spec = (Icon, label, val) => (
    <div className="border border-white/10 p-4 bg-white/[0.02]" data-testid={`public-spec-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1"><Icon className="w-4 h-4 text-[#FFB800]" /><p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{label}</p></div>
      <p className="font-mono text-xl">{val || "—"}</p>
    </div>
  );

  return (
    <div className="grain relative bg-[#0D0E11] min-h-screen text-white" data-testid="public-machine-page">
      <header className="border-b border-white/10">
        <div className="max-w-[820px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#FFB800] flex items-center justify-center"><Wrench className="w-5 h-5 text-black" strokeWidth={2.5} /></div>
            <span className="font-head font-900 text-2xl tracking-tight">IRONLOG</span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Public record</span>
        </div>
      </header>

      <main className="max-w-[820px] mx-auto px-6 py-12">
        {error ? (
          <div className="card p-12 text-center" data-testid="public-error">
            <Wrench className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
            <p className="font-body text-[#94A3B8]">{error}</p>
          </div>
        ) : !m ? (
          <p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-widest text-[#FFB800] mb-2">{m.type}{m.fleet_number ? ` · ${m.fleet_number}` : ""}</p>
            <h1 className="font-head font-900 uppercase tracking-tighter text-4xl sm:text-5xl leading-none mb-8" data-testid="public-title">{m.make} {m.model}</h1>

            {m.flagged && (
              <div className="flex items-start gap-2 border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-4 mb-8" data-testid="public-flagged-banner">
                <AlertTriangle className="w-5 h-5 text-[#FF3B30] mt-0.5 shrink-0" />
                <div>
                  <p className="font-head font-800 uppercase tracking-wide text-[#FF3B30]">Flagged — Fault Reported</p>
                  <p className="font-body text-sm text-[#94A3B8] mt-1">{m.flag_reason || "This machine has an outstanding pre-start fault."}</p>
                </div>
              </div>
            )}

            {result ? (
              <div className={`border p-5 mb-8 ${result.overall === "fail" ? "border-[#FF3B30]/50 bg-[#FF3B30]/10" : "border-[#22C55E]/50 bg-[#22C55E]/10"}`} data-testid="prestart-result">
                <div className="flex items-center gap-2">
                  {result.overall === "fail" ? <AlertTriangle className="w-5 h-5 text-[#FF3B30]" /> : <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />}
                  <p className="font-head font-800 uppercase tracking-wide">{result.overall === "fail" ? "Pre-start logged — fault flagged" : "Pre-start logged — all clear"}</p>
                </div>
                <p className="font-body text-sm text-[#94A3B8] mt-1">{result.overall === "fail" ? (result.work_order_created ? "An urgent work order has been raised and the manager notified." : "The manager has been notified.") : "Thanks — you're good to go."}</p>
              </div>
            ) : showPreStart ? (
              <div className="mb-8">
                <PreStartForm machineId={id} onDone={onPreStartDone} onCancel={() => setShowPreStart(false)} />
              </div>
            ) : (
              <button onClick={() => setShowPreStart(true)} className="btn-signal h-12 px-6 font-head inline-flex items-center gap-2 mb-8" data-testid="start-prestart-btn">
                <ClipboardCheck className="w-5 h-5" /> Log a Pre-Start Check
              </button>
            )}

            {m.has_photo && (
              <div className="mb-8" data-testid="public-gallery">
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2">
                  {Array.from({ length: m.photo_count || 1 }).map((_, i) => (
                    <img
                      key={i}
                      src={`${API}/public/machines/${id}/photo/${i}`}
                      alt={`${m.make} ${m.model} photo ${i + 1}`}
                      className="snap-center shrink-0 w-full max-h-96 object-cover border border-white/10"
                      data-testid={`public-photo-${i}`}
                    />
                  ))}
                </div>
                {(m.photo_count || 0) > 1 && <p className="font-mono text-[11px] text-[#94A3B8] mt-1">{m.photo_count} photos · swipe to view</p>}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
              {spec(Clock, "Running Hours", `${m.running_hours || 0}h`)}
              {spec(Gauge, "Odometer", `${m.odometer_km || 0} km`)}
              {spec(Wrench, "Year", m.year)}
            </div>

            <div className="card">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">
                <ClipboardList className="w-5 h-5 text-[#FFB800]" />
                <h2 className="font-head font-700 text-xl uppercase tracking-wide">Service History</h2>
              </div>
              <div className="px-5 py-4">
                <div className="flex gap-8 mb-4">
                  <div><p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Total Services</p><p className="font-head font-900 text-3xl text-[#FFB800]" data-testid="public-service-count">{m.service_summary.count}</p></div>
                  <div><p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Last Service</p><p className="font-head font-700 text-xl">{m.service_summary.last_service_date ? fmtDate(m.service_summary.last_service_date) : "—"}</p></div>
                </div>
                {m.service_summary.recent.length > 0 ? (
                  <div className="divide-y divide-white/10 border-t border-white/10">
                    {m.service_summary.recent.map((s, i) => (
                      <div key={i} className="flex items-center justify-between py-3" data-testid={`public-service-${i}`}>
                        <span className="font-head font-700 uppercase">{s.type}</span>
                        <span className="font-mono text-xs text-[#94A3B8]">{s.date ? fmtDate(s.date) : "—"}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="font-body text-sm text-[#94A3B8]">No services logged yet.</p>}
              </div>
            </div>

            <p className="font-body text-xs text-[#94A3B8] mt-8 max-w-lg">
              This is a public summary shown when a machine&apos;s QR code is scanned. Sensitive details (serial number,
              registration, costs and notes) are not shown here.
            </p>
            <div className="mt-8">
              <Link to="/" className="btn-signal h-12 px-6 font-head inline-flex items-center gap-2" data-testid="public-cta">Manage your fleet with IronLog</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
